import React, { useState, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import {
  useGetLowStockProductsQuery,
  useUpdateStockMutation,
} from "state/api";
import {
  DataTable,
  TableToolbar,
  StatusChip,
  normalizeList,
} from "components/shared";

const stockChip = (p) => {
  const stock = Number(p.stock ?? 0);
  const threshold = Number(p.lowStockThreshold ?? p.minStock ?? 5);
  if (stock <= 0) return <StatusChip status="cancelled" label="Out" />;
  if (stock <= threshold) return <StatusChip status="pending" label="Low" />;
  return <StatusChip status="active" label="In Stock" />;
};

const RequestList = () => {
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState({ open: false, product: null });
  const [addQty, setAddQty] = useState("");
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  const { data, isFetching } = useGetLowStockProductsQuery();
  const [updateStock, { isLoading: isSaving }] = useUpdateStockMutation();

  const rows = useMemo(() => {
    const all = normalizeList(data).rows;
    if (!search) return all;
    const q = search.toLowerCase();
    return all.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.productCode || p.code || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  const openRestock = (product) => {
    setAddQty("");
    setDialog({ open: true, product });
  };
  const closeRestock = () => setDialog({ open: false, product: null });

  const handleSave = async () => {
    const qty = Number(addQty);
    if (!dialog.product || !qty || qty <= 0) return;
    try {
      await updateStock({
        id: dialog.product._id,
        quantity: qty,
        type: "add",
      }).unwrap();
      setSnack({
        open: true,
        severity: "success",
        message: `Added ${qty} unit(s) to ${dialog.product.name}.`,
      });
      closeRestock();
    } catch (err) {
      setSnack({
        open: true,
        severity: "error",
        message:
          err?.data?.error?.message ||
          err?.data?.message ||
          err?.data?.errors?.[0]?.msg ||
          "Failed to update stock",
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        render: (p) => (
          <>
            <Typography variant="body2" fontWeight={600}>
              {p.name}
            </Typography>
            {(p.productCode || p.code) && (
              <Typography variant="caption" color="text.secondary">
                {p.productCode || p.code}
              </Typography>
            )}
          </>
        ),
      },
      {
        field: "category",
        headerName: "Category",
        render: (p) => p.category?.name || p.category || "—",
      },
      {
        field: "stock",
        headerName: "Current Stock",
        render: (p) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              {Number(p.stock ?? 0)}
            </Typography>
            {stockChip(p)}
          </Stack>
        ),
      },
      {
        field: "threshold",
        headerName: "Threshold",
        align: "right",
        render: (p) => p.lowStockThreshold ?? p.minStock ?? 5,
      },
      {
        field: "actions",
        headerName: "",
        align: "right",
        render: (p) => (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddBoxIcon />}
            onClick={() => openRestock(p)}
          >
            Restock
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <>
      <TableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search low-stock products…"
      />
      <DataTable
        columns={columns}
        rows={rows}
        loading={isFetching}
        emptyText="No low-stock products — everything is well stocked"
      />

      <Dialog open={dialog.open} onClose={closeRestock} fullWidth maxWidth="xs">
        <DialogTitle>Restock {dialog.product?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current stock: {dialog.product?.stock ?? 0}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="Quantity to add"
            value={addQty}
            onChange={(e) => setAddQty(e.target.value)}
            inputProps={{ min: 1 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRestock}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || !addQty || Number(addQty) <= 0}
          >
            Add Stock
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RequestList;
